import { motion, useAnimation, Variants } from 'framer-motion';
import { useEffect, useRef } from 'react';
import type { OverlayAnimation } from '@/types/story.types';

interface FramedOverlayProps {
  frameAssetUrl: string;        // URL to frame PNG with transparent center
  contentImageUrl: string;      // URL to content image (goes inside frame)
  animation?: OverlayAnimation; // Animation preset
  opacity?: number;             // Overall opacity
  contentPosition?: 'center' | 'fill' | 'contain';
  onEnterComplete?: () => void;
  onExitComplete?: () => void;
}

/**
 * FramedOverlay - Generic component for displaying images within decorative frames
 *
 * Architecture:
 * - Z-30: Frame PNG (polaroid, border, hands, etc.)
 * - Z-40: Content Image (positioned within frame's transparent area)
 *
 * Story-agnostic: Frame assets are story-specific, component handles composition/animation
 */
export function FramedOverlay({
  frameAssetUrl,
  contentImageUrl,
  animation = 'slide-up-breath',
  opacity = 1.0,
  contentPosition = 'contain',
  onEnterComplete,
  onExitComplete,
}: FramedOverlayProps) {
  const controls = useAnimation();
  const hasPlayedEnter = useRef(false);

  // Animation variants for different presets
  const slideUpBreathVariants: Variants = {
    initial: {
      y: '100%',
      opacity: 0
    },
    enter: {
      y: '0%',
      opacity: opacity,
      transition: {
        type: 'spring',
        stiffness: 80,      // Lower = slower, more weighted
        damping: 20,        // Lower = more bounce
        mass: 1.5,          // Higher = heavier feel
        duration: 1.2
      }
    },
    breathing: {
      y: [0, -5, 0],
      rotate: [0, 0.5, -0.5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
        repeatType: 'loop'
      }
    },
    exit: {
      y: '100%',
      opacity: 0,
      transition: {
        duration: 0.6,
        ease: 'easeIn'
      }
    }
  };

  const fadeVariants: Variants = {
    initial: {
      opacity: 0,
      scale: 0.95
    },
    enter: {
      opacity: opacity,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: 'easeIn'
      }
    }
  };

  const zoomInVariants: Variants = {
    initial: {
      scale: 0.3,
      opacity: 0
    },
    enter: {
      scale: 1,
      opacity: opacity,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 25,
        duration: 0.8
      }
    },
    exit: {
      scale: 0.8,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: 'easeIn'
      }
    }
  };

  const noneVariants: Variants = {
    initial: { opacity: 0 },
    enter: {
      opacity: opacity,
      transition: { duration: 0.1 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.1 }
    }
  };

  // Select variants based on animation type
  const selectedVariants =
    animation === 'slide-up-breath' ? slideUpBreathVariants :
    animation === 'fade' ? fadeVariants :
    animation === 'zoom-in' ? zoomInVariants :
    noneVariants;

  // Play animation sequence on mount
  useEffect(() => {
    const playSequence = async () => {
      if (hasPlayedEnter.current) return;
      hasPlayedEnter.current = true;

      // Start with initial state
      await controls.set('initial');

      // Animate to enter state
      await controls.start('enter');
      onEnterComplete?.();

      // If using slide-up-breath, start breathing loop
      if (animation === 'slide-up-breath') {
        controls.start('breathing');
      }
    };

    playSequence();
  }, [animation, controls, onEnterComplete]);

  // Determine object-fit based on contentPosition
  const objectFitClass = {
    'center': 'object-contain',
    'fill': 'object-cover',
    'contain': 'object-contain'
  }[contentPosition];

  return (
    <motion.div
      className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center p-4 sm:p-8 md:p-12"
      variants={selectedVariants}
      initial="initial"
      animate={controls}
      exit="exit"
      onAnimationComplete={(definition) => {
        if (definition === 'exit') {
          onExitComplete?.();
        }
      }}
    >
      {/* Container for frame + content - sized by frame image */}
      <div className="relative flex items-center justify-center" style={{ maxWidth: '90vw', maxHeight: '80vh' }}>
        {/* Frame Layer - determines overall size */}
        <img
          src={frameAssetUrl}
          alt="Frame"
          className="relative w-full h-full object-contain pointer-events-none select-none"
          style={{
            filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5))'
          }}
          onLoad={() => console.log('[FramedOverlay] Frame loaded:', frameAssetUrl)}
          onError={(e) => {
            console.error('[FramedOverlay] Failed to load frame:', frameAssetUrl);
            console.error('[FramedOverlay] Error:', e);
          }}
        />

        {/* Content Layer - absolutely positioned within frame */}
        <div className="absolute inset-0 flex items-center justify-center" style={{
          // Adjust padding to account for frame borders/hands
          // This centers content in the frame's transparent area
          padding: '12% 8%'
        }}>
          <img
            src={contentImageUrl}
            alt="Content"
            className={`w-full h-full ${objectFitClass}`}
            style={{
              filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3))'
            }}
            onLoad={() => console.log('[FramedOverlay] Content loaded:', contentImageUrl)}
            onError={(e) => {
              console.error('[FramedOverlay] Failed to load content:', contentImageUrl);
              console.error('[FramedOverlay] Error:', e);
            }}
          />
        </div>
      </div>
    </motion.div>
  );
}
